import { useCallback, useRef, useState } from "react";
import { Box, GlobalStyles, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment, { type Moment } from "moment";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useReportPdfExport } from "@/shared/hooks/useReportPdfExport";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { getReportPdf } from "@/shared/repositories/ReportPdfRepository";
import { gridToPdfTable } from "@/shared/utils/gridToPdfTable";
import { formatProxyDate } from "@/shared/utils/proxyDateTime";
import { useNewRolls } from "../hooks/useNewRolls";
import type { NewRollRow } from "../models/NewRolls";
import { NewRollsSummary } from "./NewRollsSummary";
import { NewRollsToolbar } from "./NewRollsToolbar";

const CENTER_CELL = { textAlign: "center" } as const;
const LEFT_CELL = { textAlign: "left" } as const;
const CENTER_HEADER = "ag-cell-center-header";

const toFixedOrEmpty = (value: unknown, decimals: number): string =>
	value == null ? "" : Number(value).toFixed(decimals);

const columnDefs: ColDef<NewRollRow>[] = [
	{
		field: "Name",
		headerName: "Foundry Number",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Description",
		headerName: "Description",
		// Never filled in the legacy (0 of 4,105 rolls); the Rolls page can now set it.
		width: 150,
		sortable: true,
		cellStyle: LEFT_CELL,
		tooltipField: "Description",
	},
	{
		field: "Diameter",
		headerName: "Diameter",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
	{
		field: "Width",
		headerName: "Width",
		width: 110,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
	{
		field: "RollApplication",
		headerName: "Roll Application",
		// Up to 30 characters (STDS 14V & 16V VERTICAL ONLY).
		flex: 1,
		minWidth: 260,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "RollMaterial",
		headerName: "Roll Material",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "MillSize",
		headerName: "Mill Size",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "CostCenter",
		headerName: "Cost Center",
		width: 140,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Supplier",
		headerName: "Supplier",
		// The longest supplier, MILLER CENTRIFUGAL CASTING CO, on one line.
		flex: 1,
		minWidth: 260,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Cost",
		headerName: "Cost",
		width: 110,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 2),
	},
	{
		field: "OrderTimestamp",
		headerName: "Order Timestamp",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatProxyDate(p.value),
	},
	{
		field: "ReceiveTimestamp",
		headerName: "Receive Timestamp",
		width: 160,
		sortable: true,
		sort: "asc",
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatProxyDate(p.value),
	},
];

const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
};

export const NewRollsReport = () => {
	const [{ start, end }, setFilters, { resetValues, isDefault }] =
		useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<NewRollRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useNewRolls({
		start: startStr,
		end: endStr,
	});

	const rows = dataQuery.data ?? [];

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: `New_Rolls_${startStr}_${endStr}.xlsx`,
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (colId === "OrderTimestamp" || colId === "ReceiveTimestamp") {
					return formatProxyDate(params.value);
				}
				if (colId === "Diameter" || colId === "Width") {
					return toFixedOrEmpty(params.value, 3);
				}
				if (colId === "Cost") {
					return toFixedOrEmpty(params.value, 2);
				}
				return params.value ?? "";
			},
		});
	}, [startStr, endStr]);

	const { exporting: exportingPdf, exportPdf } =
		useReportPdfExport(setErrorMsg);
	const handleExportToPdf = () => {
		const api = gridApiRef.current;
		if (!api) return;
		const table = gridToPdfTable(api, {
			countLabel: "Total Rolls",
			sums: [{ colId: "Cost", label: "Total Cost" }],
			aligns: {
				Description: "left",
				Diameter: "right",
				Width: "right",
				Cost: "right",
			},
		});
		exportPdf(
			`New Rolls ${start.format("MM-DD-YYYY")} to ${end.format("MM-DD-YYYY")}.pdf`,
			() =>
				getReportPdf({
					title: "New Rolls",
					filters: [
						{ label: "Start Date", value: start.format("MM/DD/YYYY") },
						{ label: "Close Date", value: end.format("MM/DD/YYYY") },
					],
					tables: [table],
				}),
		);
	};

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				New Rolls
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<NewRollsToolbar
					onReset={() => resetValues()}
					canReset={!isDefault()}
					start={start}
					end={end}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					onExportExcel={handleExportToExcel}
					onExportPdf={handleExportToPdf}
					canExportPdf={!dataQuery.isLoading && rows.length > 0}
					exportingPdf={exportingPdf}
				/>
			</Paper>

			<NewRollsSummary
				total={rows.length}
				money={{
					label: "Total Cost",
					value: rows.reduce((sum, row) => sum + (Number(row.Cost) || 0), 0),
				}}
			/>

			<Paper
				elevation={3}
				sx={{
					flex: 1,
					overflow: "hidden",
				}}
			>
				{dataQuery.isLoading ? (
					<CenteredLazyLoading />
				) : (
					<AgGridReact<NewRollRow>
						columnDefs={columnDefs}
						rowData={rows}
						rowModelType="clientSide"
						defaultColDef={{ resizable: true }}
						suppressContextMenu
						suppressCellFocus
						headerHeight={32}
						rowHeight={28}
						onGridReady={(e) => {
							gridApiRef.current = e.api;
						}}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
